import React, { useState, useEffect, useMemo, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowLeft, Lock } from 'lucide-react';
import './PaymentStep.css';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const PaymentForm = ({ booking, chosenAmount, isFull, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // The PaymentElement iframe can take a moment to mount even after
  // stripe+elements resolve. Iteration_54 caught the Pay button
  // saying 'Pay £5.00' and being enabled while the iframe hadn't
  // rendered yet — a click there failed with a generic Stripe
  // IntegrationError. Track the element's own `ready` event and gate
  // the button on it too.
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Set busy state IMMEDIATELY so the customer sees feedback even if
    // stripe.confirmPayment hangs. Iteration_53 caught silent first
    // clicks with no spinner / no error — usually because Stripe.js
    // had not finished loading and !stripe silently no-op'd here.
    if (processing) return;
    setError(null);
    if (!stripe || !elements || !ready) {
      setError('Payment is still loading. Please try again in a moment.');
      return;
    }
    setProcessing(true);
    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location.origin}/booking?payment_success=true` },
        redirect: 'if_required',
      });
      if (submitError) {
        setError(submitError.message || 'Payment failed. Please try again.');
        setProcessing(false);
      } else {
        onSuccess();
      }
    } catch (unexpected) {
      // Anything Stripe.js throws instead of returning error.
      console.error('Unexpected confirmPayment throw:', unexpected);
      setError('Something went wrong taking payment. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <button type="button" className="back-btn" onClick={onBack}>
        <ArrowLeft size={16} /> Back
      </button>

      <h2>{isFull ? 'Pay in full' : 'Pay Deposit'}</h2>
      <div className="deposit-amount">
        <span className="label">{isFull ? 'Amount:' : 'Deposit amount:'}</span>
        <span className="amount" data-testid="payment-amount-display">£{chosenAmount.toFixed(2)}</span>
      </div>

      <div className="booking-summary-mini">
        <p><strong>{booking.serviceName}</strong></p>
        <p>{booking.barberName} · {booking.time}</p>
      </div>

      <div className="payment-element-wrapper">
        {/* Default the billing address country to GB so the postcode
            field accepts UK formats (SW1A 1AA), not the US 5-digit
            ZIP that Elements defaults to. Iteration_53 flagged this
            as blocking real UK customer payments. */}
        <PaymentElement
          options={{
            defaultValues: {
              billingDetails: { address: { country: 'GB' } },
            },
            fields: { billingDetails: { address: 'auto' } },
          }}
          onReady={() => setReady(true)}
        />
      </div>

      {error && (
        <div className="payment-error" data-testid="payment-error" role="alert">
          {error}
        </div>
      )}

      <button
        type="submit"
        className="btn-pay"
        disabled={!stripe || !elements || !ready || processing}
        data-testid="payment-pay-btn"
      >
        <Lock size={16} />
        {processing
          ? 'Processing...'
          : (!stripe || !elements || !ready)
            ? 'Loading payment...'
            : `Pay £${chosenAmount.toFixed(2)}`}
      </button>

      <div className="secure-notice">
        <Lock size={14} />
        <span>Secure payment powered by Stripe</span>
      </div>
    </form>
  );
};

const PaymentStep = ({ booking, onSuccess, onBack }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Payment mode — 'deposit' or 'full'. When `allowPayInFull` is false
  // the picker is skipped and we straight to Elements with deposit.
  const allowFull = !!booking.allowPayInFull && Number(booking.fullAmount || 0) > Number(booking.depositAmount || 0);
  const [mode, setMode] = useState(allowFull ? null : 'deposit'); // null forces the picker first

  const chosenAmount = mode === 'full' ? Number(booking.fullAmount) : Number(booking.depositAmount);
  const isFull = mode === 'full';

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Guard against React 18 StrictMode double-invocation AND fast
  // re-renders while a fetch is already in flight. Without this we
  // hit `/payment/create-intent` twice per mode change; the second
  // response overwrote bookings.stripe_payment_intent_id while
  // Elements was already mounted with the first client_secret, so
  // the PI the customer actually paid was orphaned from the row
  // (iteration_52 root cause). The backend now also reuses a
  // non-terminal PI for the same booking as belt-and-braces.
  const inFlightForMode = useRef(null);

  useEffect(() => {
    if (!mode) return;
    if (inFlightForMode.current === mode) return;
    inFlightForMode.current = mode;
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setClientSecret(null);
    setConnectedAccountId(null);
    (async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/stripe/payment/create-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: ac.signal,
          body: JSON.stringify({
            // SEC-003 fix: amount is now derived server-side from
            // service + shop. Sending it here would be ignored.
            booking_id: booking.bookingId,
            currency: 'gbp',
            pay_in_full: isFull,
          }),
        });
        const data = await response.json();
        if (response.ok && data.success) {
          setClientSecret(data.client_secret);
          setConnectedAccountId(data.connected_account_id);
        } else {
          setError(data.detail || 'Failed to initialize payment');
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error creating payment intent:', err);
        setError('Failed to initialize payment');
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      ac.abort();
      // Clear the guard on cleanup so a genuine mode change re-fetches.
      if (inFlightForMode.current === mode) inFlightForMode.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const stripePromise = useMemo(() => {
    if (!connectedAccountId) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: connectedAccountId });
  }, [connectedAccountId]);

  // Mode picker — shown first when a full-pay option exists.
  if (allowFull && !mode) {
    return (
      <div className="booking-step payment-step" data-testid="payment-mode-picker">
        <button type="button" className="back-btn" onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </button>
        <h2>Choose how to pay</h2>
        <div className="booking-summary-mini" style={{ marginBottom: 18 }}>
          <p><strong>{booking.serviceName}</strong></p>
          <p>{booking.barberName} · {booking.time}</p>
        </div>

        <div className="pay-mode-options" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            type="button"
            className="pay-mode-card"
            onClick={() => setMode('deposit')}
            data-testid="payment-mode-deposit"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '18px 20px', borderRadius: 12, cursor: 'pointer',
              border: '1.5px solid var(--line)', background: '#fff',
              textAlign: 'left', fontFamily: 'inherit',
            }}
          >
            <span>
              <b style={{ fontSize: 15 }}>Pay deposit now</b>
              <br />
              <small style={{ color: 'var(--mute)', fontSize: 12.5 }}>
                Balance of £{(booking.fullAmount - booking.depositAmount).toFixed(2)} on the day
              </small>
            </span>
            <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--acc, #4f46ff)' }}>
              £{Number(booking.depositAmount).toFixed(2)}
            </span>
          </button>

          <button
            type="button"
            className="pay-mode-card"
            onClick={() => setMode('full')}
            data-testid="payment-mode-full"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '18px 20px', borderRadius: 12, cursor: 'pointer',
              border: '1.5px solid var(--acc, #4f46ff)',
              background: 'rgba(79, 70, 255, 0.04)',
              textAlign: 'left', fontFamily: 'inherit',
            }}
          >
            <span>
              <b style={{ fontSize: 15 }}>Pay in full now</b>
              <br />
              <small style={{ color: 'var(--mute)', fontSize: 12.5 }}>
                Nothing to pay on the day
              </small>
            </span>
            <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--acc, #4f46ff)' }}>
              £{Number(booking.fullAmount).toFixed(2)}
            </span>
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="booking-step" data-testid="payment-loading">
        <div className="payment-loading">Setting up secure payment...</div>
      </div>
    );
  }

  if (error) {
    // If the mode picker was in play, take the user back there so they
    // can retry with a different amount instead of losing step-4 state.
    const errorBack = allowFull ? () => { setError(null); setMode(null); } : onBack;
    return (
      <div className="booking-step">
        <div className="payment-error" data-testid="payment-init-error">{error}</div>
        <button className="btn" onClick={errorBack} data-testid="payment-error-back-btn">Go Back</button>
      </div>
    );
  }

  if (!clientSecret || !stripePromise) return null;

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: { colorPrimary: '#4f46ff', borderRadius: '13px' },
    },
  };

  const backToPicker = allowFull ? () => setMode(null) : onBack;

  return (
    <div className="booking-step payment-step" data-testid="payment-step">
      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          booking={booking}
          chosenAmount={chosenAmount}
          isFull={isFull}
          onSuccess={onSuccess}
          onBack={backToPicker}
        />
      </Elements>
    </div>
  );
};

export default PaymentStep;
